import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import {
  authService,
  type DevUpdateRoleRequest,
  type LoginRequest,
  type RegisterRequest,
  type UpdateProfileRequest,
} from '@/services';
import { applyUserPreferences } from '@/lib/user-preferences';

export function useAuth() {
  const { user, isAuthenticated, setAuth, clearAuth, updateUser } = useAuthStore();

  return {
    user,
    isAuthenticated,
    setAuth,
    clearAuth,
    updateUser,
  };
}

export function useLogin() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  return useMutation({
    mutationFn: async (data: LoginRequest) => {
      const response = await authService.login(data);
      return response;
    },
    onSuccess: async (response) => {
      setAuth(
        {
          id: '',
          email: '',
          displayName: null,
          role: 'CUSTOMER',
          isActive: true,
          createdAt: new Date().toISOString(),
        },
        response.accessToken,
      );
      // Fetch user profile after login
      const profile = await authService.getProfile();
      setAuth(profile, response.accessToken);
      // Aplicar idioma y moneda guardados antes de navegar
      await applyUserPreferences(profile.preferredLanguage, profile.preferredCurrency);
      navigate('/profile');
    },
    onError: () => {
      // Error is handled by the mutation error
    },
  });
}

export function useRegister() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  return useMutation({
    mutationFn: async (data: RegisterRequest) => {
      const response = await authService.register(data);
      return response;
    },
    onSuccess: async (response) => {
      setAuth(
        {
          id: '',
          email: '',
          displayName: null,
          role: 'CUSTOMER',
          isActive: true,
          createdAt: new Date().toISOString(),
        },
        response.accessToken,
      );
      // Fetch user profile after registration
      const profile = await authService.getProfile();
      setAuth(profile, response.accessToken);
      // Aplicar idioma y moneda guardados antes de navegar
      await applyUserPreferences(profile.preferredLanguage, profile.preferredCurrency);
      navigate('/profile');
    },
    onError: () => {
      // Error is handled by the mutation error
    },
  });
}

export function useLogout() {
  const navigate = useNavigate();
  const { clearAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await authService.logout();
    },
    onSuccess: () => {
      clearAuth();
      queryClient.clear();
      navigate('/login');
    },
  });
}

export function useProfile() {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['profile'],
    queryFn: () => authService.getProfile(),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useUpdateProfile() {
  const { updateUser } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateProfileRequest) => {
      return authService.updateProfile(data);
    },
    onSuccess: (profile) => {
      updateUser(profile);
      queryClient.setQueryData(['profile'], profile);
    },
  });
}

export function useDevUpdateRole() {
  const { updateUser } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: DevUpdateRoleRequest) => {
      return authService.devUpdateRole(data);
    },
    onSuccess: (profile) => {
      updateUser(profile);
      queryClient.setQueryData(['profile'], profile);
    },
  });
}
